/* hs-sections-c.js — behaviours for hs-journey ([data-hs-journey]) and the hs-stats count-up
   ([data-hs-countup]). One owner per behaviour (R-36); booted once per page and again on
   shopify:section:load.
   Scroll path: reads only scrollY / innerHeight and writes one attribute on the smallest element
   whose subtree the CSS reads (R-89). Geometry is cached in DOCUMENT coordinates and re-measured
   OFF the scroll path — boot, load, 400/1400/2600 ms settles, width-only resize (R-71), a
   ResizeObserver on <html>. Nothing fires on an untrusted layout (R-135): trust = window load,
   or the document height held across two 450 ms samples, or the user scrolled ≥ 40 px.
   The count-up is belted with scroll-position math, so it resolves without its
   IntersectionObserver (R-134), and a section already scrolled past is resolved, never skipped
   (R-121). A body-pinning scroll lock makes scrollY lie, so nothing runs under one (R-80). */
(function () {
  'use strict';
  if (window.__hsSectionsC) return;
  window.__hsSectionsC = true;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileMQ = window.matchMedia('(max-width: 991px)');
  var isMobile = mobileMQ.matches;
  var design = !!(window.Shopify && window.Shopify.designMode);
  var journeys = [];
  var counters = [];
  var trusted = document.readyState === 'complete';
  var raf = 0;
  var measureRaf = 0;
  var lastW = window.innerWidth;

  function num(v, d) { var n = parseFloat(v); return isNaN(n) ? d : n; }
  function pinned() { return !!(document.body && document.body.style.position === 'fixed'); }

  /* ---- hs-journey: the source's isScrolledIntoView2, verbatim ----------------------------
     A stage is reached while  viewportBottom >= stageBottom + offset  (offset 200 px in the
     source), and un-reached again on the way back up — so the dots, fill and bars follow the
     reader in both directions. The offset is a section setting (desktop + mobile). */
  function bootJourney(root) {
    if (root.__hsJ) return;
    var j = {
      root: root,
      off: num(root.getAttribute('data-off'), 200),
      offM: num(root.getAttribute('data-off-m'), 200),
      items: []
    };
    [].forEach.call(root.querySelectorAll('[data-hs-stage]'), function (el) {
      j.items.push({ el: el, bottom: Infinity, on: el.hasAttribute('data-hs-on') });
    });
    root.__hsJ = j;
    journeys.push(j);
  }
  function syncJourney(j, vb) {
    var off = isMobile ? j.offM : j.off;
    for (var i = 0; i < j.items.length; i++) {
      var it = j.items[i];
      var on = vb >= it.bottom + off;
      if (on === it.on) continue;
      it.on = on;
      if (on) it.el.setAttribute('data-hs-on', ''); else it.el.removeAttribute('data-hs-on');
    }
  }

  /* ---- hs-stats count-up: the source's parseStatTarget + animateStat, verbatim -------------
     prefix + number + suffix ("2,000+", "7,000mg", "1M+"); easeOutQuad; 1000 ms when the target
     is ≤ 100, else 1800 ms; every stat in the section starts together once 30 % of the section is
     visible. One deliberate change: the last frame writes the merchant's text exactly, and digit
     grouping is used only if the text had it. Reduced motion and the theme editor keep the
     server-rendered final value and never animate. */
  function parseStat(el) {
    var raw = (el.getAttribute('data-hs-count') || '').trim();
    var m = raw.match(/^(\D*)([\d.,\s]+)(.*)$/);
    if (!m) return null;
    var numeric = m[2].replace(/[\s,]/g, '');
    var hasDec = numeric.indexOf('.') !== -1;
    var target = hasDec ? parseFloat(numeric) : parseInt(numeric, 10);
    if (isNaN(target)) return null;
    var dec = hasDec ? (numeric.split('.')[1] || '').length : 0;
    var group = m[2].indexOf(',') !== -1;
    function fmt(v) {
      if (dec > 0) return v.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec, useGrouping: group });
      return Math.round(v).toLocaleString('en-US', { useGrouping: group });
    }
    return { el: el, raw: raw, prefix: m[1], suffix: m[3], target: target, fmt: fmt };
  }
  function bootCount(root) {
    if (root.__hsC) return;
    var c = { root: root, stats: [], top: Infinity, h: 0, done: false, io: null };
    root.__hsC = c;
    if (reduce.matches || design) return;
    [].forEach.call(root.querySelectorAll('[data-hs-count]'), function (el) {
      var p = parseStat(el);
      if (p) c.stats.push(p);
    });
    if (!c.stats.length) return;
    c.stats.forEach(function (p) { p.el.textContent = p.prefix + p.fmt(0) + p.suffix; });
    counters.push(c);
    if ('IntersectionObserver' in window) {
      c.io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          /* an early (untrusted) intersection is deferred: the position sweep re-checks it once trusted */
          if (entries[i].isIntersecting && trusted && !pinned()) fireCount(c);
        }
      }, { threshold: 0.3 });
      c.io.observe(root);
    }
  }
  function stopCount(c) {
    c.done = true;
    if (c.io) { c.io.disconnect(); c.io = null; }
  }
  function fireCount(c) {
    if (c.done) return;
    stopCount(c);
    c.stats.forEach(animateStat);
  }
  function finishCount(c) {
    if (c.done) return;
    stopCount(c);
    c.stats.forEach(function (p) { p.el.textContent = p.raw; });
  }
  function animateStat(p) {
    var duration = p.target <= 100 ? 1000 : 1800;
    var start = performance.now();
    function ease(t) { return 1 - (1 - t) * (1 - t); }
    function step(now) {
      var t = Math.min((now - start) / duration, 1);
      if (t < 1) {
        p.el.textContent = p.prefix + p.fmt(ease(t) * p.target) + p.suffix;
        requestAnimationFrame(step);
      } else {
        p.el.textContent = p.raw;
      }
    }
    requestAnimationFrame(step);
  }

  /* ---- measure (off the scroll path) + sweep (the scroll path) ------------------------------ */
  function measure() {
    measureRaf = 0;
    if (pinned()) return;
    var y = window.scrollY;
    for (var i = 0; i < journeys.length; i++) {
      var items = journeys[i].items;
      for (var k = 0; k < items.length; k++) items[k].bottom = items[k].el.getBoundingClientRect().bottom + y;
    }
    for (var n = 0; n < counters.length; n++) {
      var c = counters[n];
      if (c.done) continue;
      var r = c.root.getBoundingClientRect();
      c.top = r.top + y;
      c.h = r.height;
    }
    update();
  }
  function scheduleMeasure() { if (!measureRaf) measureRaf = requestAnimationFrame(measure); }
  function update() {
    raf = 0;
    if (!trusted || pinned()) return;
    var y = window.scrollY;
    var vh = window.innerHeight;
    var vb = y + vh;
    for (var i = 0; i < journeys.length; i++) syncJourney(journeys[i], vb);
    for (var k = 0; k < counters.length; k++) {
      var c = counters[k];
      if (c.done || c.h === 0) continue;
      if (c.top + c.h <= y) { finishCount(c); continue; }
      var visible = Math.min(vb, c.top + c.h) - Math.max(y, c.top);
      if (visible >= 0.3 * Math.min(c.h, vh)) fireCount(c);
    }
  }
  function trust() {
    if (trusted) return;
    trusted = true;
    scheduleMeasure();
  }
  function onScroll() {
    if (!trusted && window.scrollY >= 40) trust();
    if (!raf) raf = requestAnimationFrame(update);
  }

  function init() {
    journeys = journeys.filter(function (j) { return j.root.isConnected; });
    counters = counters.filter(function (c) { return c.root.isConnected; });
    [].forEach.call(document.querySelectorAll('[data-hs-journey]'), bootJourney);
    [].forEach.call(document.querySelectorAll('[data-hs-countup]'), bootCount);
    scheduleMeasure();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function () { setTimeout(init, 0); });
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('load', function () {
    trust();
    scheduleMeasure();
    [400, 1400, 2600].forEach(function (t) { setTimeout(scheduleMeasure, t); });
  });
  window.addEventListener('resize', function () {
    if (window.innerWidth === lastW) return;
    lastW = window.innerWidth;
    scheduleMeasure();
  });
  window.addEventListener('pageshow', function (e) { if (e.persisted) scheduleMeasure(); });
  var onMQ = function () { isMobile = mobileMQ.matches; scheduleMeasure(); };
  if (mobileMQ.addEventListener) mobileMQ.addEventListener('change', onMQ); else if (mobileMQ.addListener) mobileMQ.addListener(onMQ);
  if ('ResizeObserver' in window) new ResizeObserver(function () { scheduleMeasure(); }).observe(document.documentElement);
  (function sample(prev, n) {
    if (trusted) return;
    var h = document.documentElement.scrollHeight;
    if (n > 0 && h === prev) { trust(); return; }
    setTimeout(function () { sample(h, n + 1); }, 450);
  })(-1, 0);
})();
